import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Building2, Database, Plus, Loader2, Star, CheckCircle2,
  AlertCircle, Trash2, ExternalLink, MapPin,
} from 'lucide-react';

const PRIMARY = 'primary';

export default function Destinations() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ qbo_profiles: [], odoo_profiles: [], bindings: [] });
  const [locations, setLocations] = useState([]);
  const [dialog, setDialog] = useState({ open: false, kind: null }); // 'qbo' or 'odoo'
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([
        api.listDestinations(token),
        api.listLocations(token, false),
      ]);
      setData(d.data);
      setLocations(l.data?.locations || []);
    } catch (e) {
      toast.error('Could not load destinations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const openAdd = (kind) => { setName(''); setDialog({ open: true, kind }); };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (dialog.kind === 'qbo') await api.createQboProfile(token, trimmed);
      else await api.createOdooProfile(token, trimmed);
      toast.success(`${dialog.kind === 'qbo' ? 'QuickBooks' : 'Odoo'} destination added`);
      setDialog({ open: false, kind: null });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create destination');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (kind, profile_id) => {
    if (profile_id === PRIMARY) {
      toast.error('The primary destination can only be disconnected, not deleted');
      return;
    }
    if (!window.confirm('Remove this destination and unlink it from every location?')) return;
    setBusyId(`${kind}:${profile_id}`);
    try {
      if (kind === 'qbo') await api.deleteQboProfile(token, profile_id);
      else await api.deleteOdooProfile(token, profile_id);
      toast.success('Destination removed');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    } finally {
      setBusyId(null);
    }
  };

  const locNameById = Object.fromEntries((locations || []).map(l => [l.location_id, l.location_name]));

  return (
    <div className="space-y-6" data-testid="destinations-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading text-white">Destinations</h1>
          <p className="text-sm text-slate-400 mt-1">
            Route each location's sales to its own QuickBooks company or Odoo database.
            Multi-brand groups can keep separate books while the Dashboard stays consolidated.
          </p>
        </div>
        <Link to="/locations">
          <Button variant="outline" className="border-slate-700 text-slate-200 hover:text-white" data-testid="go-to-locations">
            <MapPin className="w-4 h-4 mr-1.5" /> Manage locations
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DestinationColumn
            kind="qbo"
            title="QuickBooks Online"
            icon={<Building2 className="w-5 h-5 text-emerald-300" />}
            profiles={data.qbo_profiles}
            setupPath="/quickbooks"
            locNameById={locNameById}
            onAdd={() => openAdd('qbo')}
            onRemove={(id) => remove('qbo', id)}
            busyId={busyId}
          />
          <DestinationColumn
            kind="odoo"
            title="Odoo ERP"
            icon={<Database className="w-5 h-5 text-purple-300" />}
            profiles={data.odoo_profiles}
            setupPath="/odoo"
            locNameById={locNameById}
            onAdd={() => openAdd('odoo')}
            onRemove={(id) => remove('odoo', id)}
            busyId={busyId}
          />
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 sm:max-w-md" data-testid="destination-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">
              Add a {dialog.kind === 'qbo' ? 'QuickBooks company' : 'Odoo database'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Give this destination a memorable name. You'll finish the
              {dialog.kind === 'qbo' ? ' Intuit OAuth flow' : ' Odoo credentials wizard'}
              {' '}in the next step.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-slate-300">Display name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={dialog.kind === 'qbo' ? 'e.g. Sunrise Coffee LLC' : 'e.g. Odoo — HQ'}
              className="bg-slate-950 border-slate-800 text-white"
              data-testid="destination-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-700 text-slate-200" onClick={() => setDialog({ open: false, kind: null })}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="destination-save-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create destination
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DestinationColumn({ kind, title, icon, profiles, setupPath, locNameById, onAdd, onRemove, busyId }) {
  return (
    <Card className="bg-slate-900/60 border-slate-800">
      <CardHeader className="border-b border-slate-800">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {icon}
            <CardTitle className="text-white font-heading">{title}</CardTitle>
          </div>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white" onClick={onAdd} data-testid={`add-${kind}-btn`}>
            <Plus className="w-4 h-4 mr-1.5" /> Add
          </Button>
        </div>
        <CardDescription className="text-slate-400 text-sm">
          Every connected {kind === 'qbo' ? 'company' : 'database'} for this tenant.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-3 space-y-2">
        {(!profiles || profiles.length === 0) && (
          <div className="text-center py-8 text-slate-500 text-sm">
            No destinations yet. Click <strong>Add</strong> to attach one.
          </div>
        )}
        {(profiles || []).map((p) => (
          <div
            key={p.profile_id}
            className="border border-slate-800 rounded-lg p-3 hover:border-slate-700 transition-all"
            data-testid={`destination-${kind}-${p.profile_id}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium truncate">{p.profile_name}</span>
                  {p.is_default && (
                    <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 gap-1 text-[10px]">
                      <Star className="w-3 h-3" /> Primary
                    </Badge>
                  )}
                  {p.connected ? (
                    <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 gap-1 text-[10px]">
                      <CheckCircle2 className="w-3 h-3" /> Connected
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-800 text-slate-400 border-slate-700 gap-1 text-[10px]">
                      <AlertCircle className="w-3 h-3" /> Not connected
                    </Badge>
                  )}
                </div>
                {p.company_name && (
                  <div className="text-xs text-slate-400 mt-0.5 truncate">{p.company_name}</div>
                )}
                {p.database && (
                  <div className="text-xs text-slate-500 font-mono mt-0.5 truncate">
                    {p.url} · {p.database}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {p.is_default ? (
                  <Link to={setupPath}>
                    <Button size="sm" variant="outline" className="h-8 border-slate-700 text-slate-200 hover:text-white" data-testid={`setup-${kind}-${p.profile_id}`}>
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> {p.connected ? 'Manage' : 'Connect'}
                    </Button>
                  </Link>
                ) : (
                  <Link to={`${setupPath}?profile_id=${encodeURIComponent(p.profile_id)}`}>
                    <Button size="sm" variant="outline" className="h-8 border-slate-700 text-slate-200 hover:text-white" data-testid={`setup-${kind}-${p.profile_id}`}>
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> {p.connected ? 'Manage' : 'Connect'}
                    </Button>
                  </Link>
                )}
                {!p.is_default && (
                  <Button
                    size="sm" variant="outline"
                    className="h-8 border-rose-500/40 text-rose-300 hover:text-white hover:bg-rose-500/10"
                    onClick={() => onRemove(p.profile_id)}
                    disabled={busyId === `${kind}:${p.profile_id}`}
                    data-testid={`delete-${kind}-${p.profile_id}`}
                  >
                    {busyId === `${kind}:${p.profile_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </Button>
                )}
              </div>
            </div>
            {p.bound_location_ids && p.bound_location_ids.length > 0 && (
              <div className="mt-2 pt-2 border-t border-slate-800 flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase tracking-wide text-slate-500 mr-1">Locations:</span>
                {p.bound_location_ids.map((lid) => (
                  <Badge key={lid} className="bg-slate-800 border-slate-700 text-slate-300 text-[10px]">
                    <MapPin className="w-3 h-3 mr-1" /> {locNameById[lid] || lid.slice(0, 6)}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
